import { __decorate } from "tslib";
import { Model, modelRegistry } from './Model';
import { Order, User, CashRegister, CashRegisterTransaction, TaxRate, Product, ProductCategory, Shift, Table } from '../models';
import { ReportTypeLabel, PaymentTypeLabel, OrderFlagType } from '../types';
import EinvoiceService from '../services/EinvoiceService';
import ApiService from '../services/ApiService';
import Printable from '../print/Printable';
import Store from '../wrappers/Store';
import Money from '../utilities/Money';
import { I18n, LocaleOverride } from '../wrappers/I18n';
import { dateFormatter } from '../utilities/dateFormatter';
import moment from 'moment-timezone';
const { getTimezone, formatDate, formatDateTime } = dateFormatter();
export class Report extends Model {
    static entity = 'reports';
    static priceFormat = '0,0.00';
    id = null;
    type = null;
    startTime = null;
    endTime = null;
    year = null;
    cashRegisterId = null;
    filterOrders = {};
    orders = null;
    transactions = [];
    data = {};
    width = '';
    sap = undefined;
    cashRegister;
    // Optional overrides from IReportOptions - when provided, use these instead of Store.get()
    isOnline;
    venue;
    isRetail;
    static fields() {
        return {
            id: this.attr(null),
            type: this.attr(null),
            startTime: this.attr(null, value => value ? moment(value) : null),
            endTime: this.attr(null, value => value ? moment(value) : null),
            year: this.attr(null),
            cashRegisterId: this.attr(null),
            filterOrders: this.attr({}),
            orders: this.attr(null),
            transactions: this.attr([]),
            data: this.attr({}),
            width: this.attr(''),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            cashRegister: this.attr(null),
            // Optional sap URL override (used by SAP app)
            sap: this.attr(undefined),
            // Optional overrides from IReportOptions
            isOnline: this.attr(undefined),
            venue: this.attr(undefined),
            isRetail: this.attr(undefined)
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    // Helper methods to get value from instance property or Store
    getIsOnline() {
        return this.isOnline ?? Store.get('isOnline') ?? false;
    }
    getVenue() {
        return this.venue ?? Store.get('tenant')?.venue;
    }
    getIsRetail() {
        return this.isRetail ?? Store.get('isRetail') ?? false;
    }
    configure() {
        this.cashRegister = CashRegister.find(this.cashRegisterId);
        const timezone = getTimezone();
        this.transactions = !this.startTime || !this.endTime ? [] : CashRegisterTransaction.all().filter(transaction => {
            const issuedAt = moment.utc(transaction.issued_at).tz(timezone);
            return issuedAt.isSameOrAfter(this.startTime) && issuedAt.isSameOrBefore(this.endTime);
        });
        if (this.orders)
            return;
        this.orders = !this.startTime || !this.endTime ? [] : Order.all().filter(order => {
            const lastActivityAt = moment.utc(order.last_activity_at).tz(timezone);
            const inRange = lastActivityAt.isSameOrAfter(this.startTime) && lastActivityAt.isSameOrBefore(this.endTime);
            if (!inRange) {
                return false;
            }
            if (this.filterOrders.cashRegisterId && this.filterOrders.cashRegisterId != order.cash_register_id) {
                return false;
            }
            if (this.filterOrders.clientId && this.filterOrders.clientId != order.client_id) {
                return false;
            }
            if (this.filterOrders.product_type && !order.items.some(i => i.getProduct()?.type === this.filterOrders.product_type)) {
                return false;
            }
            return true;
        });
    }
    static types(options = {}) {
        const { admin = false, isOnline: isOnlineOption, isRetail: isRetailOption, integrations: integrationsOption, modules: modulesOption } = options;
        const isRetail = isRetailOption ?? Store.get('isRetail');
        const isOnline = isOnlineOption ?? Store.get('isOnline');
        const integrations = integrationsOption ?? Store.get('integrations') ?? {};
        const modules = modulesOption ?? Store.get('modules') ?? {};
        return [
            {
                type: ReportTypeLabel.current_state,
                processMode: 'fiscal',
                category: I18n.t('reports.category.fiscal'),
                name: I18n.t('reports.current_state'),
                timeOptions: ['today'],
                enabled: true,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.daily_report,
                processMode: 'fiscal',
                category: I18n.t('reports.category.fiscal'),
                name: I18n.t('reports.daily_report'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.periodic_report,
                processMode: 'fiscal',
                category: I18n.t('reports.category.fiscal'),
                name: I18n.t('reports.periodic_report'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.shift_report,
                processMode: 'fiscal',
                category: I18n.t('reports.category.fiscal'),
                name: I18n.t('reports.shift_report'),
                timeOptions: ['shift'],
                // Any recent shift can be picked online (the admin is always online); offline only the open one.
                enabled: admin || !!isOnline || !!Shift.current(),
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.sales_by_user_detailed,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_user_detailed'),
                timeOptions: ['shift'],
                enabled: !admin,
                fetchData: false,
                shared: false
            },
            {
                type: ReportTypeLabel.total_sales,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.total_sales'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.sales_by_client,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_client'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.sales_by_user,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_user'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: isOnline,
                fetchData: true,
                shared: false
            },
            {
                type: ReportTypeLabel.sales_by_product,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_product'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: isOnline,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.sales_by_product_category,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_product_category'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: true,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.sales_by_product_type,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_product_type'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !isRetail,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.sales_by_product_type_sent_to_room,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_product_type_sent_to_room'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                // 'ota_sync' = frozen room-charge feature-gate key.
                enabled: !isRetail && integrations.ota_sync,
                fetchData: true,
                shared: false
            },
            {
                type: ReportTypeLabel.sales_by_product_type_and_taxes,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_product_type_and_taxes'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !isRetail,
                fetchData: true,
                shared: true
            },
            {
                type: ReportTypeLabel.sales_by_sector,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_sector'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: isOnline && !isRetail,
                fetchData: true,
                shared: false
            },
            {
                type: ReportTypeLabel.sales_by_table,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_table'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: isOnline && !isRetail,
                fetchData: true,
                shared: false
            },
            {
                type: ReportTypeLabel.sales_by_ingredient,
                processMode: 'sales',
                category: I18n.t('reports.category.sales'),
                name: I18n.t('reports.sales_by_ingredient'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !!modules.product_ingredients && isOnline && !isRetail,
                fetchData: true,
                shared: false
            },
            {
                type: ReportTypeLabel.product_quantities,
                processMode: 'default',
                category: I18n.t('reports.category.other'),
                name: I18n.t('reports.product_quantities'),
                timeOptions: [],
                enabled: true,
                fetchData: true,
                shared: false
            },
            {
                type: ReportTypeLabel.bar_ledger,
                processMode: 'custom',
                category: I18n.t('reports.category.other'),
                name: I18n.t('reports.bar_ledger'),
                timeOptions: ['today', 'yesterday', 'byDate'],
                enabled: !isRetail,
                fetchData: true,
                width: '900px',
                shared: false
            }
        ];
    }
    getHeader() {
        return {
            operator_code: this.data.operator_code || Store.get('user').hash,
            date: this.data.date || moment().format('YYYY-MM-DD'),
            time: this.data.time || moment().format('HH:mm:ss'),
            start_time: this.data.start_time || this.startTime?.format('YYYY-MM-DD HH:mm:ss') || null,
            end_time: this.data.end_time || this.endTime?.format('YYYY-MM-DD HH:mm:ss') || null,
        };
    }
    getBasicInfo() {
        if (this.data.basic_info)
            return this.data.basic_info;
        const venue = this.getVenue();
        let enu = '';
        if (this.cashRegister) {
            enu = (this.cashRegister.name ? this.cashRegister.name + ' / ' : '') + this.cashRegister.hash;
        }
        return {
            tax_payer_name: venue?.company_name || '',
            tax_payer_address: venue?.company_address || '',
            tax_id_number: venue?.tax_id_number || '',
            venue: venue?.name || '',
            venue_address: venue?.address || '',
            enu: enu
        };
    }
    getCashCalculation() {
        if (this.data.cash_calculation)
            return this.data.cash_calculation;
        const paid = this.orders.filter(order => order.isPaid());
        const advance = this.orders.filter(order => order.isAdvance() && !order.flagsInclude('closeAdvance') && !order.isAutomaticAdvanceClosing());
        const summary = this.orders.filter(order => order.isSummary());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        const deposits = this.transactions.filter(transaction => transaction.getAmount().toUnit() >= 0);
        const withdraws = this.transactions.filter(transaction => transaction.getAmount().toUnit() < 0);
        const totalCashRegisterDeposit = deposits.reduce((acc, cur) => acc.add(cur.getAmount()), Money.parse(0));
        const totalCashRegisterWithdraws = withdraws.reduce((acc, cur) => acc.add(cur.getAmount().multiply(-1)), Money.parse(0));
        const totalSalesCash = [...paid, ...summary, ...advance].reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0));
        const totalSalesCard = [...paid, ...summary, ...advance].reduce((acc, cur) => acc.add(cur.getPaidCreditCard()), Money.parse(0));
        const totalNonCashSales = [...paid, ...summary, ...advance].reduce((acc, cur) => acc.add(cur.getNonCashSale()), Money.parse(0));
        const totalCashRegisterCash = Money.parse(totalCashRegisterDeposit.toUnit()).subtract(totalCashRegisterWithdraws).add(totalSalesCash);
        const totalSalesOrders = I18n.c('order.unpaidIssuesReceipt')
            ? [...paid, ...advance, ...unpaid]
            : [...paid, ...advance];
        return {
            total_deposits: totalCashRegisterDeposit.toFormat(Report.priceFormat),
            total_sales: totalSalesOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
            cash_sales_wo_banknotes: totalSalesCard.toFormat(Report.priceFormat),
            cash_sales_banknotes: totalSalesCash.toFormat(Report.priceFormat),
            non_cash_sales: totalNonCashSales.toFormat(Report.priceFormat),
            total_withdraws: totalCashRegisterWithdraws.toFormat(Report.priceFormat),
            total_cash: totalCashRegisterCash.toFormat(Report.priceFormat),
            // Open orders only: an externally settled round (room charge) is not outstanding.
            order_sales: unpaid.filter(order => !order.isSettledExternally()).reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
        };
    }
    getSalesBreakdown() {
        if (this.data.sales_breakdown)
            return this.data.sales_breakdown;
        const paidOrders = this.orders.filter(order => order.isPaid() || order.isSummary() || order.isAdvance());
        const cashOrders = paidOrders.filter(order => !order.isNonCash());
        const nonCashOrders = paidOrders.filter(order => order.isNonCash());
        return {
            total_sales: {
                to_pay: paidOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                price: paidOrders.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0)).toFormat(Report.priceFormat),
                discount_amount: paidOrders.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            cash_sales: {
                to_pay: cashOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                price: cashOrders.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0)).toFormat(Report.priceFormat),
                discount_amount: cashOrders.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            non_cash_sales: {
                to_pay: nonCashOrders.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                price: nonCashOrders.reduce((acc, cur) => acc.add(cur.getPrice()), Money.parse(0)).toFormat(Report.priceFormat),
                discount_amount: nonCashOrders.reduce((acc, cur) => acc.add(cur.getDiscountAmount()), Money.parse(0)).toFormat(Report.priceFormat),
            }
        };
    }
    getOrderBreakdown() {
        if (this.data.order_breakdown)
            return this.data.order_breakdown;
        const corrective = this.orders.filter(order => order.correctsOthers());
        const advance = this.orders.filter(order => order.isAdvance());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        const offline = this.orders.filter(order => !order.id);
        const einvoiceSending = this.orders.filter(order => order.einvoiceIsSending());
        const einvoiceSent = this.orders.filter(order => order.einvoiceIsSent());
        const einvoiceApproved = this.orders.filter(order => order.einvoiceIsApproved());
        const einvoiceRejected = this.orders.filter(order => order.einvoiceIsRejected());
        const einvoiceCancelled = this.orders.filter(order => order.einvoiceIsCancelled());
        const einvoiceStorno = this.orders.filter(order => order.einvoiceIsStorno());
        return {
            corrective: {
                count: corrective.length,
                to_pay: corrective.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: corrective.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            advance: {
                count: advance.length,
                to_pay: advance.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: advance.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            unpaid: {
                count: unpaid.length,
                to_pay: unpaid.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: unpaid.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            offline: {
                count: offline.length,
                to_pay: offline.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: offline.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            sending: {
                count: einvoiceSending.length,
                to_pay: einvoiceSending.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: einvoiceSending.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            sent: {
                count: einvoiceSent.length,
                to_pay: einvoiceSent.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: einvoiceSent.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            approved: {
                count: einvoiceApproved.length,
                to_pay: einvoiceApproved.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: einvoiceApproved.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            rejected: {
                count: einvoiceRejected.length,
                to_pay: einvoiceRejected.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: einvoiceRejected.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            cancelled: {
                count: einvoiceCancelled.length,
                to_pay: einvoiceCancelled.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: einvoiceCancelled.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            storno: {
                count: einvoiceStorno.length,
                to_pay: einvoiceStorno.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tax: einvoiceStorno.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
            },
            manual_register: {
                count: 0,
                to_pay: Money.parse(0).toFormat(Report.priceFormat),
                tax: Money.parse(0).toFormat(Report.priceFormat),
            },
        };
    }
    getTaxes() {
        if (this.data.taxes)
            return this.data.taxes;
        const rates = TaxRate.all().map(taxRate => taxRate.rate);
        const orderTaxes = this.orders.flatMap(order => order.taxes);
        const taxes = rates.map(rate => {
            const filtered = orderTaxes.filter(tax => tax.rate == rate);
            const totalTax = filtered.reduce((acc, cur) => acc.add(Money.parse(cur.value)), Money.parse(0));
            const totalPriceBeforeTax = filtered.reduce((acc, cur) => acc.add(Money.parse(cur.price_before_tax)), Money.parse(0));
            const sales = Money.parse(totalTax.toUnit()).add(totalPriceBeforeTax);
            return {
                rate: rate,
                total_tax: totalTax,
                total_price_before_tax: totalPriceBeforeTax,
                total: sales,
            };
        });
        taxes.push({
            rate: 'total',
            total_tax: taxes.reduce((acc, cur) => acc.add(cur.total_tax), Money.parse(0)),
            total_price_before_tax: taxes.reduce((acc, cur) => acc.add(cur.total_price_before_tax), Money.parse(0)),
            total: taxes.reduce((acc, cur) => acc.add(cur.total), Money.parse(0)),
        });
        return taxes.map(tax => {
            return {
                rate: tax.rate,
                total_tax: tax.total_tax.toFormat(Report.priceFormat),
                total_price_before_tax: tax.total_price_before_tax.toFormat(Report.priceFormat),
                total: tax.total.toFormat(Report.priceFormat),
            };
        });
    }
    getPayments() {
        if (this.data.payments)
            return this.data.payments;
        const paid = this.orders.filter(order => order.isMoneyExchanged());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        return {
            cash: {
                banknote: paid.reduce((acc, cur) => acc.add(cur.getPaidCash()), Money.parse(0)).toFormat(Report.priceFormat),
                card: paid.reduce((acc, cur) => acc.add(cur.getPaidCreditCard()), Money.parse(0)).toFormat(Report.priceFormat),
                order: unpaid.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                tip: paid.reduce((acc, cur) => acc.add(cur.getTip()), Money.parse(0)).toFormat(Report.priceFormat),
                other_cash: Money.parse(0).toFormat(Report.priceFormat),
            },
            noncash: {
                account: paid.reduce((acc, cur) => acc.add(cur.getPaidInvoice()), Money.parse(0)).toFormat(Report.priceFormat),
                business_card: paid.reduce((acc, cur) => acc.add(cur.getPaidCreditCardBusiness()), Money.parse(0)).toFormat(Report.priceFormat),
                advance: paid.reduce((acc, cur) => acc.add(cur.getPaidAdvance()), Money.parse(0)).toFormat(Report.priceFormat),
                check: paid.reduce((acc, cur) => acc.add(cur.getPaidCheck()), Money.parse(0)).toFormat(Report.priceFormat),
                voucher: paid.reduce((acc, cur) => acc.add(cur.getPaidVoucher()), Money.parse(0)).toFormat(Report.priceFormat),
                mobile_money: paid.reduce((acc, cur) => acc.add(cur.getPaidMobileMoney()), Money.parse(0)).toFormat(Report.priceFormat),
                other: paid.reduce((acc, cur) => acc.add(cur.getPaidOtherNoncash()), Money.parse(0)).toFormat(Report.priceFormat),
                company: Money.parse(0).toFormat(Report.priceFormat),
                order: Money.parse(0).toFormat(Report.priceFormat),
                factoring: Money.parse(0).toFormat(Report.priceFormat),
            }
        };
    }
    getTotalSales() {
        const sales = [];
        const paid = this.orders.filter(order => order.isPaid());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        const orders = I18n.c('order.unpaidIssuesReceipt') ? [...paid, ...unpaid] : paid;
        const items = orders.flatMap(o => o.items);
        const salesGroup = { ...Report.calculateSales(items), group: 'inventory_item' };
        sales.push(salesGroup);
        return sales;
    }
    getSalesByClient() {
        const sales = [];
        const paid = this.orders.filter(order => order.isPaid());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        const orders = I18n.c('order.unpaidIssuesReceipt') ? [...paid, ...unpaid] : paid;
        const items = orders.flatMap(o => o.items);
        const salesGroup = { ...Report.calculateSales(items), group: 'inventory_item' };
        sales.push(salesGroup);
        return sales;
    }
    getSalesByProductCategory() {
        const sales = [];
        const paid = this.orders.filter(order => order.isPaid());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        const orders = I18n.c('order.unpaidIssuesReceipt') ? [...paid, ...unpaid] : paid;
        ProductCategory.all().forEach(category => {
            const items = orders.flatMap(o => o.items)
                .filter(item => {
                const product = Product.find(item.product_id);
                return product && product.category_id === category.id;
            });
            const salesGroup = { ...Report.calculateSales(items), group: category.name };
            sales.push(salesGroup);
        });
        return sales;
    }
    getSalesByProductType() {
        const sales = [];
        const paid = this.orders.filter(order => order.isPaid());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        const orders = I18n.c('order.unpaidIssuesReceipt') ? [...paid, ...unpaid] : paid;
        const appConfig = Store.get('appConfig');
        const productTypes = appConfig.product.types;
        productTypes.forEach(productType => {
            const items = orders.flatMap(o => o.items)
                .filter(item => {
                const product = Product.find(item.product_id);
                return product && product.type === productType;
            });
            const salesGroup = { ...Report.calculateSales(items), group: productType };
            sales.push(salesGroup);
        });
        return sales;
    }
    getSalesByProductTypeAndTaxes() {
        const sales = [];
        const paid = this.orders.filter(order => order.isPaid());
        const unpaid = this.orders.filter(order => order.isUnpaid());
        const orders = I18n.c('order.unpaidIssuesReceipt') ? [...paid, ...unpaid] : paid;
        const appConfig = Store.get('appConfig');
        const productTypes = appConfig.product.types;
        productTypes.forEach(productType => {
            const items = orders.flatMap(o => o.items)
                .filter(item => {
                const product = Product.find(item.product_id);
                return product && product.type === productType;
            });
            TaxRate.all().forEach(taxRate => {
                const taxRateItems = items.filter(item => parseFloat(item.tax_rate) === parseFloat(taxRate.rate));
                if (taxRateItems.length) {
                    sales.push({
                        group: `${productType}/${parseFloat(taxRate.rate)}%`,
                        items: [],
                        to_pay: taxRateItems.reduce((acc, cur) => acc.add(cur.getToPay()), Money.parse(0)).toFormat(Report.priceFormat),
                        tax: taxRateItems.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat(Report.priceFormat),
                        price_before_tax: taxRateItems.reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0)).toFormat(Report.priceFormat),
                    });
                }
            });
        });
        return sales;
    }
    getSalesByUserDetailed() {
        const sales = [];
        const userToProcess = User.find(this.filterOrders.userId);
        const users = this.filterOrders.userId
            ? userToProcess ? [userToProcess] : []
            : User.all();
        users.forEach(user => {
            const userOrders = this.orders.filter(order => {
                const userId = Number(order.getFlag(OrderFlagType.clearDebtForUser)[0]) || order.user_id;
                return userId === user.id;
            });
            const paid = userOrders.filter(order => order.isPaid());
            const unpaid = userOrders.filter(order => order.isUnpaid());
            const orders = I18n.c('order.unpaidIssuesReceipt') ? [...paid, ...unpaid] : paid;
            const items = orders.flatMap(o => o.items);
            const salesGroup = Report.calculateSales(items);
            salesGroup.group = 'inventory_item';
            salesGroup.heading = user.name;
            salesGroup.stats = Order.generateStats(userOrders);
            salesGroup.stats.user_id = user.id;
            salesGroup.stats.cash.deposit = user.getDeposit().toFormat(Report.priceFormat);
            salesGroup.stats.cash.transfered = user.getTransfered().toFormat(Report.priceFormat);
            salesGroup.stats.cash.debt_cleared = user.getCharged().multiply(-1).toFormat(Report.priceFormat);
            salesGroup.stats.cash.debt = user.getDebt().toFormat(Report.priceFormat);
            salesGroup.stats.cash.tip = userOrders.reduce((acc, cur) => acc.add(cur.getTip()), Money.parse(0)).toFormat(Report.priceFormat);
            sales.push(salesGroup);
        });
        return sales;
    }
    getProductQuantities() {
        return [
            {
                'heading': null,
                'columns': [
                    { 'key': 'inventory_item', 'align': 'L', 'textWidth': null, 'htmlWidth': '55%' },
                    { 'key': 'category', 'align': 'L', 'textWidth': 12, 'htmlWidth': '30%' },
                    { 'key': 'quantity', 'align': 'R', 'textWidth': 7, 'htmlWidth': '15%' }
                ],
                'items': Product.all().map(p => {
                    const category = ProductCategory.find(p.category_id);
                    return {
                        'key': p.name,
                        'values': [category?.name || '', p.getCount() + ''],
                        'additional': []
                    };
                }),
                'summary': []
            }
        ];
    }
    getSales() {
        if (this.data.sales)
            return this.data.sales;
        const methodName = 'get' + this.type?.replace(/(_|^)(\w)/g, (match, p1, p2) => p2.toUpperCase());
        if (typeof this[methodName] === 'function') {
            return this[methodName]();
        }
        return [];
    }
    getData() {
        if (this.data.data)
            return this.data.data;
        const methodName = 'get' + this.type?.replace(/(_|^)(\w)/g, (match, p1, p2) => p2.toUpperCase());
        if (typeof this[methodName] === 'function') {
            return this[methodName]();
        }
        return [];
    }
    static calculateSales(items) {
        let sales = {
            items: []
        };
        const products = Product.all();
        items.forEach(orderItem => {
            let addedItem = sales.items.find(item => {
                return item.product_id
                    && +item.product_id === orderItem.product_id
                    && item.unit_price === orderItem.getUnitPrice().toFormat('0.0000')
                    && item.discount_amount === orderItem.getDiscountAmount().toFormat('0.0000');
            });
            const product = products.find(product => product.id == orderItem.product_id);
            if (!addedItem) {
                sales.items.push({
                    product_id: orderItem.product_id ? orderItem.product_id + '' : null,
                    name: product?.name || orderItem.name,
                    code: product?.code || '/',
                    in_stock: product ? product.count : '/',
                    count: orderItem.getCount().toFixed(3),
                    unit_price: orderItem.getUnitPrice().toFormat('0.0000'),
                    price: orderItem.getPrice().toFormat('0.0000'),
                    to_pay: orderItem.getToPay().toFormat('0.0000'),
                    discount_amount: orderItem.getDiscountAmount().toFormat('0.0000'),
                });
            }
            else {
                addedItem.count = this.round(parseFloat(addedItem.count) + orderItem.getCount(), 3).toFixed(3);
                addedItem.price = Money.parse(addedItem.price)
                    .add(orderItem.getPrice())
                    .toFormat('0.0000');
                addedItem.to_pay = Money.parse(addedItem.to_pay)
                    .add(orderItem.getToPay())
                    .toFormat('0.0000');
                addedItem.discount_amount = Money.parse(addedItem.discount_amount)
                    .add(orderItem.getDiscountAmount())
                    .toFormat('0.0000');
            }
        });
        sales.price = sales.items
            .reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.price).toFormat('0.00'))), Money.parse(0))
            .toFormat(Report.priceFormat);
        sales.to_pay = sales.items
            .reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.to_pay).toFormat('0.00'))), Money.parse(0))
            .toFormat(Report.priceFormat);
        sales.discount_amount = sales.items
            .reduce((acc, cur) => acc.add(Money.parse(Money.parse(cur.discount_amount).toFormat('0.00'))), Money.parse(0))
            .toFormat(Report.priceFormat);
        sales.items = sales.items.map(item => {
            item.in_stock = this.round(item.in_stock, 3) + '';
            item.count = this.round(item.count, 3) + '';
            item.price = Money.parse(item.price).toFormat(Report.priceFormat);
            item.to_pay = Money.parse(item.to_pay).toFormat(Report.priceFormat);
            item.discount_amount = Money.parse(item.discount_amount).toFormat(Report.priceFormat);
            return item;
        });
        return sales;
    }
    getElectronicJournal(journalType = 'all') {
        if (this.data.journal_items)
            return this.data.journal_items;
        const orders = this.orders
            .filter(order => {
            return journalType === 'all'
                || (journalType === 'offline' && !order.id)
                || (journalType === 'corrective' && order.correctsOthers());
        }).map(order => {
            return {
                item: order.$toJson(),
                timestamp: order.getLastActivityAt()?.format(Report.DATE_FORMAT) || '',
                type: 'order'
            };
        });
        const transactions = this.transactions
            .filter(transaction => {
            if (journalType === 'corrective')
                return false;
            return journalType === 'all' || (journalType === 'offline' && !transaction.id);
        }).map(transaction => {
            return {
                item: transaction.$toJson(),
                timestamp: transaction.getIssuedAt()?.format(Report.DATE_FORMAT) || '',
                type: 'cashTransaction'
            };
        });
        return [...orders, ...transactions]
            .sort((a, b) => a.timestamp.valueOf() > b.timestamp.valueOf() ? 1 : -1);
    }
    getFooter() {
        const workingDays = Store.get('workingDays');
        let businessDayNumber = workingDays?.length && this.startTime ? workingDays.indexOf(this.startTime.format('YYYY-MM-DD')) + 1 : '';
        let firstWorkingDay = workingDays?.length && this.startTime ? workingDays.indexOf(this.startTime.format('YYYY-MM-DD')) + 1 : '';
        let lastWorkingDay = workingDays?.length && this.endTime ? workingDays.indexOf(this.endTime.format('YYYY-MM-DD')) + 1 : '';
        const footer = {};
        if (this.type === 'daily_report') {
            footer.business_day_number = this.data.business_day_number || businessDayNumber;
        }
        else if (this.type === 'periodic_report') {
            const firstOrder = this.orders?.length ? this.orders[0] : null;
            const lastOrder = this.orders?.length ? this.orders[this.orders?.length - 1] : null;
            footer.first_business_day = this.data.first_business_day || firstWorkingDay + '';
            footer.last_business_day = this.data.last_business_day || lastWorkingDay + '';
            footer.first_order_number = this.data.first_order_number || (firstOrder?.number ? firstOrder.number + '' : '');
            footer.last_order_number = this.data.last_order_number || (lastOrder?.number ? lastOrder.number + '' : '');
        }
        return footer;
    }
    static async createReport(options) {
        const { type, startTime, endTime, year = null, cashRegisterId = null, orders = null, filterOrders = {}, admin = false, sap = undefined, width = '', isOnline: isOnlineOption, venue: venueOption, isRetail: isRetailOption } = options;
        const reportType = Report.types().find(r => r.type === type);
        if (!reportType)
            return null;
        const report = new Report({
            type,
            startTime,
            endTime,
            cashRegisterId,
            year,
            orders,
            filterOrders,
            sap
        });
        // Set optional overrides
        report.isOnline = isOnlineOption;
        report.venue = venueOption;
        report.isRetail = isRetailOption;
        report.width = width;
        report.configure();
        const isOnline = report.getIsOnline();
        const generateOffline = !year && (!isOnline || !reportType.fetchData);
        if (generateOffline) {
            return report;
        }
        try {
            await report.fetchData(admin);
            return report.data ? report : null;
        }
        catch (error) {
            console.log(error.message);
            return null;
        }
    }
    async fetchData(admin = false) {
        let url = admin ? '/admin/reports' : '/report';
        if (this.sap?.url) {
            url = this.sap.url;
        }
        const params = {
            type: this.type,
            start_time: this.startTime?.format('YYYY-MM-DD HH:mm:ss') || null,
            end_time: this.endTime?.format('YYYY-MM-DD HH:mm:ss') || null,
            cash_register_id: this.cashRegisterId,
            client_id: this.filterOrders.clientId,
            table_id: this.filterOrders.tableId,
            products: this.filterOrders.products,
            product_type: this.filterOrders.product_type,
            year: this.year || undefined
        };
        const response = await ApiService.get(url, { params: params, timeout: 300000 });
        this.data = response.data;
        return response.data;
    }
    generate() {
        const reportType = Report.types().find(r => r.type === this.type);
        if (!reportType)
            return;
        let reportData = {
            ...this.getHeader(),
            ...this.getFooter(),
            type: this.type,
            basic_info: this.getBasicInfo()
        };
        if (reportType.processMode === 'sales') {
            reportData.sales = this.getSales();
        }
        else if (reportType.processMode === 'journal') {
            const journalType = this.type.replace('electronic_journal', '').replace('_', '') || 'all';
            reportData.journal_items = this.getElectronicJournal(journalType);
        }
        else if (reportType.processMode === 'fiscal') {
            reportData.order_breakdown = this.getOrderBreakdown();
            reportData.taxes = this.getTaxes();
            reportData.payments = this.getPayments();
            reportData.cash_calculation = this.getCashCalculation();
            reportData.sales_breakdown = this.getSalesBreakdown();
        }
        else {
            reportData.data = this.getData();
        }
        return reportData;
    }
    /**
     * Generate the printable object
     */
    async getPrintable() {
        const reportData = this.generate();
        if (!reportData) {
            return new Printable();
        }
        const reportType = Report.types().find(r => r.type === this.type);
        if (reportType?.processMode === 'custom') {
            const methodName = 'get' + reportType.type.replace(/(_|^)(\w)/g, (match, p1, p2) => p2.toUpperCase()) + 'Printable';
            if (typeof this[methodName] === 'function') {
                return this[methodName](reportData);
            }
            return new Printable();
        }
        const header = this.getHeaderPrintable(reportData);
        const basicInfo = this.getBasicInfoPrintable(reportData);
        const cashCalculation = this.getCashCalculationPrintable(reportData);
        const salesBreakdown = this.getSalesBreakdownPrintable(reportData);
        const orderBreakdown = this.getOrderBreakdownPrintable(reportData);
        const sales = this.getSalesPrintable(reportData);
        const data = this.getDataPrintable(reportData);
        const taxes = this.getTaxesPrintable(reportData);
        const payments = this.getPaymentsPrintable(reportData);
        const journal = await this.getJournalPrintable(reportData);
        const additionalFooter = this.getAdditionalFooterPrintable(reportData);
        const footer = this.getFooterPrintable(reportData);
        const pr = new Printable();
        pr.l('D', '', { delimiter: '=' });
        const isJournal = [
            'electronic_journal',
            'offline_electronic_journal',
            'corrective_electronic_journal'
        ].includes(this.type);
        let printableContent = isJournal ? [
            ...header.getIPrintableContent(),
            ...basicInfo.getIPrintableContent(),
            ...journal.getIPrintableContent(),
            ...additionalFooter.getIPrintableContent(),
            ...footer.getIPrintableContent(),
        ] : [
            ...header.getIPrintableContent(),
            ...basicInfo.getIPrintableContent(),
            ...cashCalculation.getIPrintableContent(),
            ...salesBreakdown.getIPrintableContent(),
            ...orderBreakdown.getIPrintableContent(),
            ...sales.getIPrintableContent(),
            ...data.getIPrintableContent(),
            ...taxes.getIPrintableContent(),
            ...payments.getIPrintableContent(),
            ...additionalFooter.getIPrintableContent(),
            ...footer.getIPrintableContent()
        ];
        pr.addPrintableContent(printableContent);
        pr.l('D', '', { delimiter: '=' });
        return pr;
    }
    getHeaderPrintable(reportData) {
        const pr = new Printable();
        pr.l('L', I18n.t('reports.' + reportData.type), { align: 'C' });
        if (reportData.start_time && reportData.end_time) {
            pr.l();
            pr.l('L', `${I18n.t('general.from')}: ${formatDateTime(reportData.start_time)}`, { align: 'C' });
            pr.l('L', `${I18n.t('general.to')}: ${formatDateTime(reportData.end_time)}`, { align: 'C' });
        }
        pr.l('D', '', { delimiter: '-' });
        return pr;
    }
    getBasicInfoPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.basic_info)
            return pr;
        pr.l('L', `${I18n.t('reports.body.basic_info.tax_payer_name')}: ${reportData.basic_info.tax_payer_name}`);
        pr.l('B', `${I18n.t('reports.body.basic_info.tax_payer_address')}:`, reportData.basic_info.tax_payer_address);
        pr.l('L', `${I18n.t('reports.body.basic_info.tax_id_number')}: ${reportData.basic_info.tax_id_number}`);
        pr.l('L', `${I18n.t('reports.body.basic_info.venue')}: ${reportData.basic_info.venue}`);
        pr.l('B', `${I18n.t('reports.body.basic_info.venue_address')}:`, reportData.basic_info.venue_address);
        if (reportData.basic_info.enu) {
            pr.l('L', `${I18n.t('reports.body.basic_info.enu')}: ${reportData.basic_info.enu}`);
        }
        pr.l('D', '', { delimiter: '-' });
        return pr;
    }
    getCashCalculationPrintable(reportData) {
        const pr = new Printable();
        const noncashOnly = !!this.getVenue()?.settings?.noncash_only;
        const einvoiceReport = reportData.type === 'einvoice_report';
        if (!reportData.cash_calculation || noncashOnly || einvoiceReport)
            return pr;
        pr.l('S', `${I18n.t('reports.body.cash_calculation.total_cash')}:`, reportData.cash_calculation.total_cash);
        pr.l('S', `${I18n.t('reports.body.cash_calculation.deposit')}:`, reportData.cash_calculation.total_deposits);
        pr.l('S', `${I18n.t('reports.body.cash_calculation.total_withdraws')}:`, reportData.cash_calculation.total_withdraws);
        pr.l('S', `${I18n.t('reports.body.cash_calculation.cash_sales_banknotes')}:`, reportData.cash_calculation.cash_sales_banknotes);
        pr.l('D', '', { delimiter: '-' });
        return pr;
    }
    getSalesBreakdownPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.sales_breakdown)
            return pr;
        const noncashOnly = !!this.getVenue()?.settings?.noncash_only;
        const einvoiceReport = reportData.type === 'einvoice_report';
        if (reportData.sales_breakdown.cash_sales && !noncashOnly && !einvoiceReport) {
            pr.l('L', I18n.t('reports.body.sales_breakdown.cash_sales'), { align: 'C' });
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.price')}:`, reportData.sales_breakdown.cash_sales.price);
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.discount')}:`, reportData.sales_breakdown.cash_sales.discount_amount);
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.to_pay')}:`, reportData.sales_breakdown.cash_sales.to_pay);
            pr.l('D', '', { delimiter: '-' });
        }
        if (reportData.sales_breakdown.non_cash_sales && !noncashOnly && !einvoiceReport) {
            pr.l('L', I18n.t('reports.body.sales_breakdown.non_cash_sales'), { align: 'C' });
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.price')}:`, reportData.sales_breakdown.non_cash_sales.price);
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.discount')}:`, reportData.sales_breakdown.non_cash_sales.discount_amount);
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.to_pay')}:`, reportData.sales_breakdown.non_cash_sales.to_pay);
            pr.l('D', '', { delimiter: '-' });
        }
        if (reportData.sales_breakdown.total_sales) {
            pr.l('L', I18n.t('reports.body.sales_breakdown.total_sales'), { align: 'C' });
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.price')}:`, reportData.sales_breakdown.total_sales.price);
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.discount')}:`, reportData.sales_breakdown.total_sales.discount_amount);
            pr.l('S', `${I18n.t('reports.body.sales_breakdown.to_pay')}:`, reportData.sales_breakdown.total_sales.to_pay);
            pr.l('D', '', { delimiter: '-' });
        }
        return pr;
    }
    getOrderBreakdownPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.order_breakdown)
            return pr;
        const einvoiceReport = reportData.type === 'einvoice_report';
        const orderCategories = einvoiceReport
            ? ['sending', 'sent', 'approved', 'rejected', 'cancelled', 'storno']
            : ['corrective', 'advance', 'offline', 'manual_register'];
        if (I18n.c('order.unpaidIssuesReceipt')) {
            orderCategories.push('unpaid');
        }
        orderCategories.forEach(category => {
            if (reportData.order_breakdown?.[category]) {
                pr.l('L', `${I18n.t(`reports.body.order_breakdown.${category}`)}`, { align: 'C' });
                pr.l('D', '', { delimiter: '-' });
                pr.l('S', `${I18n.t('reports.body.order_breakdown.count')}:`, reportData.order_breakdown[category].count + '');
                pr.l('S', `${I18n.t('reports.body.order_breakdown.to_pay')}:`, reportData.order_breakdown[category].to_pay);
                pr.l('S', `${I18n.t('reports.body.order_breakdown.tax')}:`, reportData.order_breakdown[category].tax);
                pr.l('D', '', { delimiter: '-' });
            }
        });
        return pr;
    }
    getSalesPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.sales)
            return pr;
        const venue = this.getVenue();
        const noncashOnly = !!venue?.settings?.noncash_only;
        const isRetail = this.getIsRetail();
        const getTotalKeys = (salesGroup) => Object.keys(salesGroup).filter(key => {
            const value = salesGroup[key];
            return value !== null && !isNaN(Number((value + '').replace(/,/g, ''))) && key !== 'group' && key !== 'items' && key !== 'heading';
        });
        reportData.sales
            .filter(salesGroup => getTotalKeys(salesGroup).length || salesGroup.items.length || salesGroup.stats)
            .forEach(salesGroup => {
            if (salesGroup.heading) {
                pr.l('L', salesGroup.heading, { align: 'C' });
                pr.l('D', '', { delimiter: '-' });
            }
            const zero = Money.parse(0).toFormat(Report.priceFormat);
            if (salesGroup.stats && !noncashOnly) {
                if (I18n.c('general.allowLimitedTables') && !isRetail && Table.hasLimited()) {
                    pr.l('S', `${I18n.t('reports.body.sales.stats.cash.transfered')}:`, salesGroup.stats.cash.transfered || zero);
                }
                pr.l('S', `${I18n.t('reports.body.sales.stats.cash.deposit')}:`, salesGroup.stats.cash.deposit || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.cash.sold')}:`, salesGroup.stats.cash.sold || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.cash.refunded')}:`, salesGroup.stats.cash.refunded || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.cash.debt_cleared')}:`, salesGroup.stats.cash.debt_cleared || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.cash.debt')}:`, salesGroup.stats.cash.debt || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.cash.tip')}:`, salesGroup.stats.cash.tip || zero);
                pr.l('D', '', { delimiter: '-' });
                pr.l('S', `${I18n.t('reports.body.sales.stats.payments.cash')}:`, salesGroup.stats.payments.cash || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.payments.credit_card')}:`, salesGroup.stats.payments.credit_card || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.payments.invoice')}:`, salesGroup.stats.payments.invoice || zero);
                const creditCardBusinessAvailable = CashRegister.availablePaymentTypes().some(p => p.key === PaymentTypeLabel.credit_card_business);
                if (creditCardBusinessAvailable) {
                    pr.l('S', `${I18n.t('reports.body.sales.stats.payments.credit_card_business')}:`, salesGroup.stats.payments.credit_card_business || zero);
                }
                pr.l('S', `${I18n.t('reports.body.sales.stats.payments.voucher')}:`, salesGroup.stats.payments.voucher || zero);
                const checkAvailable = CashRegister.availablePaymentTypes().some(p => p.key === PaymentTypeLabel.check);
                if (checkAvailable) {
                    pr.l('S', `${I18n.t('reports.body.sales.stats.payments.check')}:`, salesGroup.stats.payments.check || zero);
                }
                const mobileMoneyAvailable = CashRegister.availablePaymentTypes().some(p => p.key === PaymentTypeLabel.mobile_money);
                if (mobileMoneyAvailable) {
                    pr.l('S', `${I18n.t('reports.body.sales.stats.payments.mobile_money')}:`, salesGroup.stats.payments.mobile_money || zero);
                }
                pr.l('S', `${I18n.t('reports.body.sales.stats.payments.other_noncash')}:`, salesGroup.stats.payments.other_noncash || zero);
                pr.l('D', '', { delimiter: '-' });
                pr.l('S', `${I18n.t('reports.body.sales.stats.payments.advance')}:`, salesGroup.stats.payments.advance || zero);
                pr.l('D', '', { delimiter: '-' });
                pr.l('S', `${I18n.t('reports.body.sales.stats.total.sold')}:`, salesGroup.stats.total.sold || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.total.refunded')}:`, salesGroup.stats.total.refunded || zero);
                pr.l('S', `${I18n.t('reports.body.sales.stats.total.unpaid')}:`, salesGroup.stats.total.unpaid || zero);
                pr.l('D', '', { delimiter: '-' });
            }
            const group = (salesGroup.group || 'other').split('/')[0];
            const groupSuffix = (salesGroup.group || '').split('/')[1];
            let groupLabel = I18n.t(`reports.body.sales.group.${group}`) !== `reports.body.sales.group.${group}`
                ? I18n.t(`reports.body.sales.group.${group}`)
                : group;
            if (groupSuffix) {
                groupLabel += ' ' + groupSuffix;
            }
            pr.l('S', groupLabel, I18n.t('general.sales'));
            pr.l('D', '', { delimiter: '-' });
            // Filter out sale items that have no count
            if (salesGroup.items.length > 0 &&
                salesGroup.items.every((item) => Object.hasOwnProperty.call(item, 'count'))) {
                salesGroup.items = salesGroup.items.filter((item) => (item.count ? +item.count : 0) !== 0);
            }
            salesGroup.items.forEach(item => {
                const itemLabel = I18n.t(`reports.body.sales.group.item.${item.name}`) !== `reports.body.sales.group.item.${item.name}`
                    ? I18n.t(`reports.body.sales.group.item.${item.name}`)
                    : item.name;
                pr.l('S', `${item.count ? this.round(item.count, 3) + ' x ' : ''}${itemLabel}`, item.price || '');
                if (item.code) {
                    pr.l('L', `    ${I18n.t('general.code')}: ${item.code}`);
                }
                if (item.discount_amount && +(item.discount_amount.replace(/,/g, '')) > 0) {
                    pr.l('L', `    ${I18n.t('general.discount')}: ${item.discount_amount}`);
                }
                if (item.additional?.length) {
                    item.additional.forEach((a) => {
                        const count = a.count ? this.round(a.count, 3) : '';
                        const price = a.price ? ` x ${this.round(a.price, 2)}` : '';
                        pr.l('L', `    ${count}${price}`);
                    });
                }
                if (venue && !venue.settings?.hide_product_quantities && item.in_stock) {
                    pr.l('L', `    ${I18n.t('general.in_stock')}: ${this.round(item.in_stock, 3)}`);
                }
                if (item.price_difference) {
                    pr.l('L', `    ${I18n.t('price_calculations.price_difference')}: ${item.price_difference}`);
                }
                if (item.purchase_value) {
                    pr.l('L', `    ${I18n.t('price_calculations.purchase_value_after_tax')}: ${item.purchase_value}`);
                }
            });
            if (salesGroup.items.length) {
                pr.l('D', '', { delimiter: '-' });
            }
            const total = getTotalKeys(salesGroup);
            if (total.length) {
                total.forEach(key => {
                    pr.l('S', `${I18n.t(`reports.body.sales.${key}`)}:`, salesGroup[key] + '');
                });
                pr.l('D', '', { delimiter: '-' });
            }
        });
        return pr;
    }
    getDataPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.data)
            return pr;
        reportData.data.forEach(dataGroup => {
            if (dataGroup.heading) {
                pr.l('L', this.t('reports.body.data.heading', dataGroup.heading) || '', { align: 'C' });
                pr.l('D', '', { delimiter: '-' });
            }
            const createTableHeading = dataGroup.columns.some(col => col.key);
            if (createTableHeading) {
                const textColWidths = dataGroup.columns.map(c => c.textWidth !== undefined ? c.textWidth : null);
                const htmlColWidths = dataGroup.columns.map(c => c.htmlWidth || null);
                const hasTextWidths = textColWidths.some(w => w !== null);
                pr.table({
                    colNumber: dataGroup.columns.length,
                    ...(hasTextWidths ? { text: { colWidths: textColWidths } } : {}),
                    html: { colWidths: htmlColWidths }
                });
                let tableHeading = '';
                dataGroup.columns.forEach(col => {
                    const key = col.key?.split('/')[0];
                    if (key) {
                        const suffix = col.key.split('/')[1];
                        let colLabel = this.t('reports.body.data.columns', key);
                        if (suffix) {
                            colLabel += ' ' + colLabel;
                        }
                        tableHeading += colLabel;
                    }
                    pr.tl({ content: tableHeading, align: col.align });
                    tableHeading = '';
                });
                pr.tl({ content: 'divider' });
            }
            dataGroup.items.forEach((item, index) => {
                item = item;
                if (item.key === 'divider') {
                    pr.tl({ content: 'divider' });
                    return;
                }
                pr.tl({ content: this.t('reports.body.data.items', item.key) || '' });
                item.values.forEach((val, index) => {
                    const align = dataGroup.columns[index + 1]?.align || 'L';
                    pr.tl({ content: val, align });
                });
                item.additional.forEach(a => {
                    pr.tl({ content: `    ${this.t('reports.body.data.items.additional', a.key)}: ${a.value}`, colSpan: dataGroup.columns.length });
                });
            });
            if (dataGroup.items.length) {
                pr.l('D', '', { delimiter: '-' });
            }
            dataGroup.summary.forEach(summaryItem => {
                pr.l('S', this.t('reports.body.data.summary', summaryItem.key) || '', summaryItem.value);
            });
            if (dataGroup.summary.length) {
                pr.l('D', '', { delimiter: '-' });
            }
        });
        return pr;
    }
    getTaxesPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.taxes)
            return pr;
        reportData.taxes.forEach(tax => {
            if (tax.rate === 'total') {
                pr.l('L', I18n.t('reports.body.taxes.sum'), { align: 'C' });
            }
            else {
                pr.l('L', `${I18n.t('reports.body.taxes.rate')} - ${tax.rate}%`, { align: 'C' });
            }
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('reports.body.taxes.total_price_before_tax')}:`, tax.total_price_before_tax);
            pr.l('S', `${I18n.t('reports.body.taxes.total_tax')}:`, tax.total_tax);
            pr.l('S', `${I18n.t('reports.body.taxes.total')}:`, tax.total);
            pr.l('D', '', { delimiter: '-' });
        });
        return pr;
    }
    getPaymentTypes(reportData) {
        const venue = this.getVenue();
        const noncashOnly = !!venue?.settings?.noncash_only;
        const einvoiceReport = reportData.type === 'einvoice_report';
        const einvoice = EinvoiceService.einvoiceOnly(venue) || einvoiceReport;
        const cashEnabled = !noncashOnly && !einvoice;
        return {
            cash: [
                { type: 'banknote', enabled: cashEnabled },
                { type: 'card', enabled: cashEnabled },
                { type: 'tip', enabled: cashEnabled },
            ],
            noncash: [
                { type: 'account', enabled: true },
                { type: 'advance', enabled: true },
                { type: 'voucher', enabled: true },
                { type: 'check', enabled: true },
                { type: 'mobile_money', enabled: true },
                { type: 'other', enabled: true },
            ]
        };
    }
    getPaymentsPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.payments)
            return pr;
        // Cash Payments
        const types = this.getPaymentTypes(reportData);
        const zero = Money.parse(0).toFormat(Report.priceFormat);
        pr.l('L', I18n.t('reports.body.payments.heading'), { align: 'C' });
        pr.l('D', '', { delimiter: '-' });
        types.cash.filter(t => t.enabled).forEach(({ type }) => {
            const t = type;
            if (reportData.payments?.cash) {
                pr.l('S', `${I18n.t(`reports.body.payments.cash.${t}`)}:`, reportData.payments.cash[t] || zero);
            }
        });
        types.noncash.filter(t => t.enabled).forEach(({ type }) => {
            const t = type;
            if (reportData.payments?.noncash) {
                pr.l('S', `${I18n.t(`reports.body.payments.noncash.${t}`)}:`, reportData.payments.noncash[t] || zero);
            }
        });
        pr.l('D', '', { delimiter: '-' });
        return pr;
    }
    async getJournalPrintable(reportData) {
        const pr = new Printable();
        if (!reportData.journal_items)
            return pr;
        for (const journalItem of reportData.journal_items) {
            if (journalItem.type === 'cashTransaction') {
                const transaction = new CashRegisterTransaction(journalItem.item);
                pr.l('L', `${I18n.t('general.time')}: ${formatDateTime(journalItem.timestamp)}`);
                if (transaction.getAmount().toUnit() >= 0) {
                    pr.l('L', `${I18n.t('reports.body.journal.deposit')}: ${transaction.getAmount().toFormat(Report.priceFormat)}`);
                }
                else {
                    pr.l('L', `${I18n.t('reports.body.journal.widraw')}: ${transaction.getAmount().multiply(-1).toFormat(Report.priceFormat)}`);
                }
                pr.l('D', '', { delimiter: '-' });
            }
            else if (journalItem.type === 'order') {
                const order = new Order(journalItem.item);
                pr.l('L', `${I18n.t('general.time')}: ${formatDateTime(journalItem.timestamp)}`);
                pr.l('L', `${I18n.t('general.receipt')}: ${order.number}`);
                // The receipt logo belongs to a printed receipt, never to the journal that lists them
                const orderIPrintableContent = (await order.getPrintable({ isReceipt: true, isCopy: true })).getIPrintableContent()
                    .filter(content => content.type !== 'LOGO');
                pr.addPrintableContent(orderIPrintableContent);
                pr.l('D', '', { delimiter: '-' });
            }
        }
        return pr;
    }
    getBarLedgerPrintable(reportData) {
        const pr = new Printable();
        if (!reportData?.data?.length)
            return pr;
        pr.l('L', '');
        pr.l('L', I18n.t('reports.body.bar_ledger.document_short'), { align: 'R' });
        pr.l('L', '');
        pr.l('L', I18n.t('reports.body.bar_ledger.document_number'), { align: 'R' });
        pr.l('L', '');
        pr.l('L', '');
        pr.l('L', I18n.t('reports.body.bar_ledger.heading').toUpperCase(), { align: 'C' });
        const fullDays = reportData.start_time?.includes('00:00:00') && reportData.end_time?.includes('23:59:59');
        let startDate = moment(reportData.start_time).format('DD.MM.YYYY.');
        let endDate = moment(reportData.end_time).format('DD.MM.YYYY.');
        if (fullDays && startDate === endDate) {
            pr.l('L', `${I18n.t('general.date').toLowerCase()}: ${startDate}`, { align: 'C' });
        }
        else {
            const period = `${moment(reportData.start_time).format('DD.MM.YYYY. HH:mm:ss')} - ${moment(reportData.end_time).format('DD.MM.YYYY. HH:mm:ss')}`;
            pr.l('L', period, { align: 'C' });
        }
        pr.l('L', '');
        pr.l('L', '');
        pr.l('L', '');
        pr.table({
            colNumber: 13,
            html: {
                colWidths: ['4%', null, '7%', '7%', '7%', '7%', '7%', '7%', '7%', '7%', '7%', '7%', '9%'],
                style: { fontSize: '12px', border: '1px solid var(--print-color)', color: 'var(--print-color)' },
            }
        });
        const headings = reportData.data[0].columns.map(column => {
            return {
                content: I18n.t(`reports.body.bar_ledger.columns.${column.key}`),
                align: column.align,
                style: {
                    padding: '4px',
                    border: '1px solid #444444',
                    verticalAlign: 'middle'
                }
            };
        });
        const cellStyle = { border: '1px solid #444444' };
        const summaryCellStyle = { border: '1px solid #444444;' };
        const subheadings = reportData.data[0].columns.map((column, index) => ({ content: '' + (index + 1), align: 'C', style: cellStyle }));
        pr.tl(...headings);
        pr.tl(...subheadings);
        reportData.data[0].items.forEach((item, rowIndex, arr) => {
            const style = rowIndex === arr.length - 1 ? summaryCellStyle : cellStyle;
            const row = item.map((colContent, colIndex) => ({ content: colContent, align: headings[colIndex].align, style }));
            pr.tl(...row);
        });
        pr.l('L', '');
        pr.l('L', '');
        pr.l('L', '');
        pr.l('L', '');
        pr.table({
            colNumber: 2,
            html: {
                style: { color: 'var(--print-color)' }
            }
        });
        pr.tl({ content: I18n.t('general.created_by'), align: 'C' }, { content: I18n.t('general.responsible_person'), align: 'C' });
        pr.tl({ content: ' ', align: 'C' }, { content: ' ', align: 'C' });
        pr.tl({ content: '______________________________', align: 'C' }, { content: '______________________________', align: 'C' });
        return pr;
    }
    getAdditionalFooterPrintable(reportData) {
        return new Printable();
    }
    getFooterPrintable(reportData) {
        const pr = new Printable();
        pr.l('L', `${I18n.t('reports.body.footer.date')}: ${formatDate(reportData.date)}`);
        pr.l('L', `${I18n.t('reports.body.footer.time')}: ${reportData.time}`);
        return pr;
    }
    async print(options = {}) {
        return (await this.getPrintable()).print({ ...options, wrapperClass: 'report' });
    }
    isSalesReport() {
        const salesReportTypes = [
            ReportTypeLabel.total_sales,
            ReportTypeLabel.sales_by_client,
            ReportTypeLabel.sales_by_table,
            ReportTypeLabel.sales_by_user,
            ReportTypeLabel.sales_by_product,
            ReportTypeLabel.sales_by_product_category,
            ReportTypeLabel.sales_by_product_type,
            ReportTypeLabel.sales_by_product_type_sent_to_room,
            ReportTypeLabel.sales_by_product_type_and_taxes,
            ReportTypeLabel.sales_by_sector,
            ReportTypeLabel.sales_by_ingredient,
            ReportTypeLabel.sales_by_user_detailed,
        ];
        return salesReportTypes.includes(this.type);
    }
    t(prefix, key) {
        return I18n.t(`${prefix}.${key}`) !== `${prefix}.${key}`
            ? I18n.t(`${prefix}.${key}`)
            : key;
    }
}
__decorate([
    LocaleOverride
], Report.prototype, "getCashCalculationPrintable", null);
__decorate([
    LocaleOverride
], Report.prototype, "getPaymentTypes", null);
__decorate([
    LocaleOverride
], Report.prototype, "getAdditionalFooterPrintable", null);
__decorate([
    LocaleOverride
], Report.prototype, "getFooterPrintable", null);
__decorate([
    LocaleOverride
], Report, "types", null);
export default Report;
modelRegistry.register('Report', Report);
//# sourceMappingURL=Report.js.map